import { Divider, Paper, Typography, Box } from "@mui/material";
import { useQuotes } from "../../../../../hooks/useQuotes";
import { useAuthContextualToken } from "../../../../../hooks/useAuthContextualToken";
import { useParams, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { ScopeOfWorkTypeOfService } from "../components/scope-of-work/ScopeOfWorkTypeOfService";
import { ScopeOfWorkAreas } from "../components/scope-of-work/ScopeOfWorkAreas";
import { ScopeOfWorkOtherAreas } from "../components/scope-of-work/ScopeOfWorkOtherAreas";
import { ScopeOfWorkAdditionalObjects } from "../components/scope-of-work/ScopeOfWorkAdditionalObjects";
import { ScopeOfWorkSpecialRequests } from "../components/scope-of-work/ScopeOfWorkSpecialRequests";
import { PaginationLoadingOverlay } from "../../../../../../clients/PaginationLoadingOverlay";
import { ScopeOfWorkTypeOfPlaces } from "../components/scope-of-work/ScopeOfWorkTypeOfPlaces";

interface Props {
  serviceId?: string;
  quoteId?: string;
}

export const ScopeOfWorkView = ({ serviceId, quoteId }: Props) => {
  const { id: idFromParams } = useParams();
  const finalId = idFromParams || quoteId;

  const { token } = useAuthContextualToken();
  const { getQuoteById, selectedQuote, loading } = useQuotes();

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const serviceUid = params.get("serviceUid") || serviceId;

  useEffect(() => {
    if (finalId && token) {
      getQuoteById(finalId, token);
    }
  }, [finalId, token]);

  if (loading || !selectedQuote) {
    return (
      <Box
        sx={{
          position: "relative",
          minHeight: "100vh",
          width: "100%",
        }}
      >
        <PaginationLoadingOverlay isVisible={loading} />

        {!loading && !selectedQuote && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "60vh",
              width: "100%",
              color: "text.secondary",
              fontSize: "0.9rem",
            }}
          >
            Quote not found or invalid ID.
          </Box>
        )}
      </Box>
    );
  }

  const service = selectedQuote.services?.find((s) => String(s.serviceUid) === String(serviceUid));

  if (!service) {
    return (
      <Box
        sx={{
          position: "relative",
          minHeight: "100vh",
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
            width: "100%",
            color: "text.secondary",
            fontSize: "0.9rem",
          }}
        >
          Service not found for UID: {serviceUid}
        </Box>
      </Box>
    );
  }

  const scope = service.scopeOfWork;

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: "100vh",
        width: "100%",
      }}
    >
      <PaginationLoadingOverlay isVisible={loading} />

      <Paper
        sx={{
          p: 3,
          maxWidth: 1200,
          mx: "auto",
          bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
          boxShadow: "none",
        }}
      >
        <Typography variant="h5" fontWeight={700} mb={2}>
          Scope of Works
        </Typography>

        <ScopeOfWorkTypeOfService
          isAdvanced={selectedQuote?.type === "advanced"}
          name={scope.name}
          typeOfService={scope.typeOfService}
          serviceNameOverride={scope.serviceNameOverride}
          serviceDescriptionOverride={scope.description}
          costOverride={scope.costOverride}
          frequencyType={scope.frequencyType}
          squareFootage={scope.squareFootage}
        />

        <ScopeOfWorkAreas areas={scope.areas} />

        <ScopeOfWorkOtherAreas list={scope.otherAreas} />

        <ScopeOfWorkAdditionalObjects list={scope.additionalObjects} />

        <ScopeOfWorkSpecialRequests list={scope.specialRequests} />
      </Paper>
    </Box>
  );
};
